import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { AlbumPicture } from '../../types';
import { tripPhotoUrl } from '../../utils/tripPhotos';
import { PHOTO_DRAG_TYPE } from './AlbumPageView';
import { Icon, Upload, X } from '../ui/Icon';
import Spinner from '../ui/Spinner';

interface OwnPhotos {
  photos: AlbumPicture[];
  uploading: boolean;
  onUpload: (files: File[]) => void;
  onRemove: (photoId: string) => void;
}

interface Props {
  photos: AlbumPicture[];
  /** ADR-0051: the photos uploaded to this album only, shown apart from the trip's. */
  own?: OwnPhotos;
  /** Photos already placed somewhere in the album, marked so they're not repeated by accident. */
  usedIds: Set<string>;
  selectedId: string | null;
  /** Null when no photo slot is selected: the grid is then only a source to drag from. */
  onPick: ((photoId: string) => void) | null;
}

const heading = 'text-[11px] font-semibold uppercase tracking-wider text-slate-500';

/** The trip's photos and the album's own, to click into the selected slot or drag onto any photo slot. */
export default function AlbumPhotoPicker({ photos, own, usedIds, selectedId, onPick }: Props) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-3 max-h-[28rem] overflow-y-auto pr-1">
      {own && (
        <section className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <h4 className={heading}>{t('album.ownPhotos.title')}</h4>
            <button onClick={() => input.current?.click()} disabled={own.uploading} className="btn-ghost-sm text-xs gap-1.5">
              {own.uploading ? <Spinner size="sm" /> : <Icon as={Upload} size={12} />}
              {t('album.ownPhotos.upload')}
            </button>
            <input
              ref={input}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={e => {
                const files = Array.from(e.target.files ?? []);
                e.target.value = '';
                if (files.length > 0) own.onUpload(files);
              }}
            />
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">{t('album.ownPhotos.hint')}</p>
          {own.photos.length > 0 && (
            <PhotoTiles photos={own.photos} usedIds={usedIds} selectedId={selectedId} onPick={onPick} onRemove={own.onRemove} />
          )}
        </section>
      )}

      <section className="space-y-1.5">
        {own && <h4 className={heading}>{t('album.ownPhotos.tripPhotos')}</h4>}
        {photos.length === 0
          ? <p className="text-xs text-slate-500 dark:text-slate-400">{t('album.noPhotos')}</p>
          : <PhotoTiles photos={photos} usedIds={usedIds} selectedId={selectedId} onPick={onPick} />}
      </section>
    </div>
  );
}

interface TilesProps {
  photos: AlbumPicture[];
  usedIds: Set<string>;
  selectedId: string | null;
  onPick: ((photoId: string) => void) | null;
  onRemove?: (photoId: string) => void;
}

function PhotoTiles({ photos, usedIds, selectedId, onPick, onRemove }: TilesProps) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-4 gap-1.5">
      {photos.map(photo => (
        <div key={photo.id} className="relative group">
          <button
            draggable
            onDragStart={e => { e.dataTransfer.setData(PHOTO_DRAG_TYPE, photo.id); e.dataTransfer.effectAllowed = 'copy'; }}
            onClick={onPick ? () => onPick(photo.id) : undefined}
            aria-label={photo.originalName}
            aria-pressed={selectedId === photo.id}
            className={`relative block w-full aspect-square overflow-hidden rounded-md ${onPick ? 'cursor-pointer' : 'cursor-grab'} ${
              selectedId === photo.id ? 'ring-2 ring-teal-500' : ''
            }`}
          >
            <img src={tripPhotoUrl(photo.thumbnailFilename ?? photo.filename)} alt="" draggable={false} className="w-full h-full object-cover" />
            {usedIds.has(photo.id) && selectedId !== photo.id && (
              <span className="absolute bottom-0.5 right-0.5 rounded bg-black/60 px-1 text-[9px] text-white">{t('album.used')}</span>
            )}
          </button>
          {onRemove && (
            <button
              onClick={() => onRemove(photo.id)}
              aria-label={t('album.ownPhotos.remove')}
              title={t('album.ownPhotos.remove')}
              className="absolute top-0.5 right-0.5 hidden group-hover:flex group-focus-within:flex w-5 h-5 items-center justify-center rounded-full bg-black/60 text-white hover:bg-red-600"
            >
              <Icon as={X} size={10} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
